// Chirp giveaways: NPCs host them and you can join (pure luck), or host your own for reach.

import type { Rng } from "../rng";
import { pushLog, type GameState } from "../state";
import { addTrust, playerClout } from "../player";
import { accountById } from "./feed";
import { TUNABLES } from "@degen/shared/config/tunables";
import { money } from "@degen/shared/money";

export interface Giveaway {
  id: string;
  host: string; // "player" | "acct:N"
  prizeUsd: number;
  startTick: number;
  endTick: number;
  entrants: number; // grows while live
  targetEntrants: number;
  joined: boolean;
  resolved: boolean;
  winner?: string; // "player" | "acct:N" | "n42"
}

export const HOST_HOURS = [6, 12, 24];

export function liveGiveaways(state: GameState): Giveaway[] {
  return state.social.giveaways.filter((g) => !g.resolved);
}

/** Your chance to win a giveaway with this many entrants (generous: it's a game). */
export function winChance(entrants: number): number {
  return Math.min(0.35, 4 / Math.sqrt(Math.max(1, entrants)));
}

export type GiveawayResult = { ok: true } | { ok: false; error: string };

export function joinGiveaway(state: GameState, id: string): GiveawayResult {
  const g = state.social.giveaways.find((x) => x.id === id);
  if (!g || g.resolved) return { ok: false, error: "That giveaway is over." };
  if (g.host === "player") return { ok: false, error: "You can't enter your own giveaway, ser." };
  if (g.joined) return { ok: false, error: "Already entered. 🤞" };
  g.joined = true;
  g.entrants++;
  if (!state.social.following.includes(g.host)) state.social.following.push(g.host); // follow + like + RT
  return { ok: true };
}

export function hostGiveaway(state: GameState, rng: Rng, prizeUsd: number, hours: number): GiveawayResult {
  const p = state.player;
  if (!(prizeUsd >= 50)) return { ok: false, error: `Minimum prize is ${money(50)}.` };
  if (!HOST_HOURS.includes(hours)) return { ok: false, error: "Pick a duration." };
  if (p.cash < prizeUsd) return { ok: false, error: "You can't give away money you don't have." };
  if (state.social.giveaways.some((g) => g.host === "player" && !g.resolved)) return { ok: false, error: "One giveaway at a time." };
  p.cash -= prizeUsd;
  const clout = playerClout(p);
  // Entrants come from your reach and the size of the prize.
  const target = Math.round((Math.pow(clout + 200, 0.6) * 2 + Math.sqrt(prizeUsd) * 6) * rng.range(0.7, 1.3));
  const g: Giveaway = { id: `gw${state.tick}p`, host: "player", prizeUsd, startTick: state.tick, endTick: state.tick + hours * 60, entrants: 0, targetEntrants: target, joined: false, resolved: false };
  state.social.giveaways.push(g);
  state.social.feed.push({
    id: `p${state.social.nextPostId++}`, tick: state.tick, author: "player", type: "meme",
    text: `🎁 GIVEAWAY: ${money(prizeUsd)} to one lucky follower! Follow + like + RT. Ends in ${hours}h 🍀`,
    likes: target, reposts: Math.round(target * 0.6), replies: Math.round(target * 0.4), replyTexts: [], giveawayId: g.id,
  });
  p.stats.posts++;
  return { ok: true };
}

/** Per tick: NPCs host now and then, entrants grow, winners get drawn. */
export function giveawaysTick(state: GameState, rng: Rng) {
  const soc = state.social;
  if (state.tick % 60 === 0 && rng.chance(0.05) && liveGiveaways(state).filter((g) => g.host !== "player").length < 3) {
    const acc = rng.pick(soc.accounts.filter((a) => a.followers > 2_000));
    const prize = Math.round(Math.min(5_000, 30 + Math.sqrt(acc.followers) * rng.range(0.5, 3)) / 10) * 10;
    const hours = rng.pick(HOST_HOURS);
    const g: Giveaway = {
      id: `gw${state.tick}${acc.id}`, host: acc.id, prizeUsd: prize, startTick: state.tick, endTick: state.tick + hours * 60,
      entrants: rng.int(5, 40), targetEntrants: Math.round(Math.sqrt(acc.followers) * rng.range(2, 8)), joined: false, resolved: false,
    };
    soc.giveaways.push(g);
    soc.feed.push({
      id: `p${soc.nextPostId++}`, tick: state.tick, author: acc.id, type: "npc",
      text: rng.pick([`🎁 ${money(prize)} giveaway! Follow + RT to enter. Winner in ${hours}h`, `giving away ${money(prize)} to one fren 🍀 like + follow`, `celebrating the pump: ${money(prize)} giveaway, ${hours}h ⏳`]),
      likes: g.targetEntrants, reposts: Math.round(g.targetEntrants * 0.5), replies: Math.round(g.targetEntrants * 0.3), replyTexts: [], giveawayId: g.id,
    });
  }

  for (const g of soc.giveaways) {
    if (g.resolved) continue;
    const frac = Math.min(1, (state.tick - g.startTick) / Math.max(1, g.endTick - g.startTick));
    g.entrants = Math.max(g.entrants, Math.round(g.targetEntrants * Math.sqrt(frac)));
    if (state.tick < g.endTick) continue;
    g.resolved = true;
    if (g.host === "player") {
      const p = state.player;
      const conv = TUNABLES.social.conversion * 0.35 * (p.trust / 100) / (1 + p.followers / TUNABLES.social.followerSaturation);
      const gained = Math.round(g.entrants * conv * 3);
      p.followers += gained;
      addTrust(p, Math.min(3, 1 + g.prizeUsd / 2_000));
      const winnerAcc = rng.pick(soc.accounts);
      g.winner = winnerAcc.id;
      pushLog(state, "giveaway_done", `🎁 Your giveaway ended: ${g.entrants.toLocaleString()} entries, @${winnerAcc.handle} won ${money(g.prizeUsd)}. +${gained.toLocaleString()} followers, trust up.`);
      continue;
    }
    const host = accountById(state, g.host);
    if (g.joined && rng.chance(winChance(g.entrants))) {
      g.winner = "player";
      state.player.cash += g.prizeUsd;
      pushLog(state, "giveaway_won", `🍀 YOU WON @${host?.handle}'s giveaway! +${money(g.prizeUsd)}`);
    } else {
      g.winner = rng.pick(soc.accounts).id;
      if (g.joined) pushLog(state, "giveaway_lost", `@${host?.handle}'s giveaway ended. Not you this time (${g.entrants.toLocaleString()} entered).`);
    }
  }
  if (soc.giveaways.length > 30) soc.giveaways = soc.giveaways.filter((g) => !g.resolved || state.tick - g.endTick < 1_440);
}
